import { FC, useId } from 'react';
import { SvgIcon, SvgIconProps } from '@mui/material';

// The four tile glyphs, redrawn from the XD artboards ("Common structure" in
// DESIGN.md): a filled document fused with a ⊕ badge, three dots with a
// magnifier, a person with a bare +, and two overlapping people. They replace
// the @mui/icons-material approximations, which the design review flagged.
//
// All shapes are solid `currentColor`, so the cut-outs must stay transparent
// over the grey and the blue tile alike. Two techniques, by case:
//  - `fillRule "evenodd"` punches a hole that lies entirely INSIDE its outer
//    subpath (text lines, the badge's +, the magnifier ring). A hole subpath
//    that leaves the outline would render as a spurious filled region.
//  - an SVG <mask> carves the silhouette of a nearer shape out of a farther
//    one (the sheet under the ⊕ badge, the front person out of the back
//    one), which no fill rule can express.

const circle = (cx: number, cy: number, r: number): string =>
  `M${cx - r} ${cy} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0 z`;

/** Document with text lines and a fused ⊕ badge — "Ajouter un document". */
export const DocumentAddIcon: FC<SvgIconProps> = (props) => {
  const mask = useId();
  return (
    <SvgIcon {...props}>
      <defs>
        {/* Clears the sheet under the badge, a shade smaller than the badge
            itself so the overlap leaves no antialiasing seam. */}
        <mask id={mask}>
          <rect width="24" height="24" fill="white" />
          <circle cx="16.6" cy="16.2" r="4.6" fill="black" />
        </mask>
      </defs>
      <path
        mask={`url(#${mask})`}
        fillRule="evenodd"
        d="M7.1 2.8 L14.6 2.8 A2.6 2.6 0 0 1 17.2 5.4 L17.2 16.4 A2.6 2.6 0 0 1 14.6 19 L7.1 19 A2.6 2.6 0 0 1 4.5 16.4 L4.5 5.4 A2.6 2.6 0 0 1 7.1 2.8 Z
           M8.3 6.6 h4.8 a0.9 0.9 0 0 1 0 1.8 h-4.8 a0.9 0.9 0 0 1 0 -1.8 z
           M8.3 10.2 h2.2 a0.9 0.9 0 0 1 0 1.8 h-2.2 a0.9 0.9 0 0 1 0 -1.8 z"
      />
      {/* ⊕ badge; its + stays inside the circle, so evenodd is safe. */}
      <path
        fillRule="evenodd"
        d={`${circle(16.6, 16.2, 4.9)}
           M15.8 13.6 h1.6 v1.8 h1.8 v1.6 h-1.8 v1.8 h-1.6 v-1.8 h-1.8 v-1.6 h1.8 z`}
      />
    </SvgIcon>
  );
};

/** Three dots and a magnifier — "Recherche détaillée". */
export const SearchDotsIcon: FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <circle cx="7.4" cy="7.4" r="3.2" />
    <circle cx="16.6" cy="7.4" r="3.2" />
    <circle cx="7.4" cy="16.6" r="3.2" />
    {/* Magnifier: ring plus a handle towards the corner. */}
    <path
      fillRule="evenodd"
      d={`${circle(16.1, 16.1, 3.3)} ${circle(16.1, 16.1, 1.65)}`}
    />
    <rect
      x="17.9"
      y="17.35"
      width="3.6"
      height="1.7"
      rx="0.85"
      transform="rotate(45 17.9 18.2)"
    />
  </SvgIcon>
);

/** Person with a + — "Ajouter un utilisateur". */
export const PersonAddIcon: FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <circle cx="10.2" cy="8.4" r="3.8" />
    <path d="M4 19.6 c0 -4 2.8 -6.5 6.2 -6.5 s6.2 2.5 6.2 6.5 z" />
    <path d="M17.6 3.6 h1.7 v2.05 h2.05 v1.7 h-2.05 v2.05 h-1.7 v-2.05 h-2.05 v-1.7 h2.05 z" />
  </SvgIcon>
);

/** Two people, one behind the other — "Liste des utilisateurs". */
export const PeopleListIcon: FC<SvgIconProps> = (props) => {
  const mask = useId();
  return (
    <SvgIcon {...props}>
      <defs>
        {/* Carves the front silhouette, grown by the gap, out of the back
            person. */}
        <mask id={mask}>
          <rect width="24" height="24" fill="white" />
          <circle cx="9.2" cy="8.8" r="4.8" fill="black" />
          <path
            d="M2.4 19.4 c0 -4.4 3 -7 6.8 -7 s6.8 2.6 6.8 7 z"
            fill="black"
          />
        </mask>
      </defs>
      <g mask={`url(#${mask})`}>
        <circle cx="15.8" cy="7.4" r="3.3" />
        <path d="M12.5 18.8 c0 -3.2 2.1 -5.3 4.8 -5.3 s4.8 2.1 4.8 5.3 z" />
      </g>
      {/* Front person. */}
      <circle cx="9.2" cy="8.8" r="3.6" />
      <path d="M3.6 19 c0 -3.8 2.6 -6.1 5.8 -6.1 s5.8 2.3 5.8 6.1 z" />
    </SvgIcon>
  );
};
